import styled from "@emotion/styled";
import { InputAdornment, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { Button, TextField } from "components/UI";
import { useChurchStore } from "features/churches";
import { useExportTitheContributions } from "features/finances";
import { useTranslation } from "react-i18next";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const SecondaryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled(MagnifyingGlass)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const TitheContributionsTable = ({
  isLoading,
  data,
  total,
  page,
  setPage,
  pageSize,
  setPageSize
}) => {
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const { t } = useTranslation();

  const { mutate: exportTitheContributions, isLoading: isExporting } =
    useExportTitheContributions();

  const columns = [
    {
      field: "full_name",
      headerName: "Full Name",
      flex: 1,
      minWidth: 300,
      sortable: false
    },
    {
      field: "jan",
      headerName: "Jan",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.January).toLocaleString()}</span>;
      }
    },
    {
      field: "feb",
      headerName: "Feb",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.February).toLocaleString()}</span>;
      }
    },
    {
      field: "mar",
      headerName: "Mar",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.March).toLocaleString()}</span>;
      }
    },
    {
      field: "apr",
      headerName: "Apr",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.April).toLocaleString()}</span>;
      }
    },
    {
      field: "may",
      headerName: "May",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.May).toLocaleString()}</span>;
      }
    },
    {
      field: "jun",
      headerName: "Jun",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.June).toLocaleString()}</span>;
      }
    },
    {
      field: "jul",
      headerName: "Jul",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.July).toLocaleString()}</span>;
      }
    },
    {
      field: "aug",
      headerName: "Aug",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.August).toLocaleString()}</span>;
      }
    },
    {
      field: "sep",
      headerName: "Sep",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.September).toLocaleString()}</span>;
      }
    },
    {
      field: "oct",
      headerName: "Oct",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.October).toLocaleString()}</span>;
      }
    },
    {
      field: "nov",
      headerName: "Nov",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.November).toLocaleString()}</span>;
      }
    },
    {
      field: "dec",
      headerName: "Dec",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.months?.December).toLocaleString()}</span>;
      }
    },
    {
      field: "total",
      headerName: "Total",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{Number(row.total).toLocaleString()}</span>;
      }
    }
  ];

  return (
    <Container>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={4}
      >
        <SecondaryActions>
          <TextField
            neutral={1}
            placeholder="Search by name"
            register={() => {}}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={16} />
                </InputAdornment>
              )
            }}
            autoComplete="off"
          />
          <Button
            onClick={() =>
              exportTitheContributions({ churchId: selectedChurch?.id })
            }
            disabled={isExporting}
          >
            Export
          </Button>
        </SecondaryActions>
      </Stack>

      <DataGrid
        autoHeight
        rows={data || []}
        rowCount={total || 0}
        loading={isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.full_name}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
      />
    </Container>
  );
};
